// Schreiben-Assistent Schritt 3: Text mit sichtbaren Platzhaltern, Eingabeleiste (Eingabefelder für alle,
// fehlende Werte je Empfänger) und Live-Vorschau des echten Brief-PDFs (Post-Layout) für den gewählten Empfänger.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowCounterClockwise, CaretLeft, CaretRight, Database, MagnifyingGlass, PencilSimple, WarningCircle } from '@phosphor-icons/react';
import { Popover } from '../../../components/immobilien/ui';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { erzeugeBlankoBriefPdf, ladePostSchrift } from '../../../lib/dokumente/blankoBrief';
import { ladePdfGestaltung, standardBriefkopf } from '../../../lib/schreiben/gestaltung';
import { useGestaltung } from './useGestaltung';
import { PLATZHALTER_LABEL, VERFUEGBARE_PLATZHALTER } from '../../../lib/dokumente/briefVorlagen';
import { datenIn, eingabeFelder, fehlend, fuelle, mitAnlagenverzeichnis, vorgabeWert } from '../../../lib/schreiben/platzhalter';
import type { EingabeFeld } from '../../../lib/schreiben/typen';
import { FELD, LABEL, Umschalter } from '../emails/bereich/Formular';
import { schluessel, werteVon, type SetZustand, type Zustand } from './zustand';

const isoZuDe = (iso: string) => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : iso);
const deZuIso = (de: string) => { const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(de); return m ? `${m[3]}-${m[2]}-${m[1]}` : ''; };
const betragDe = (roh: string) => { const n = Number(roh.replace(/\./g, '').replace(',', '.')); return Number.isFinite(n) && roh.trim() ? n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : roh; };

/** Fehlende Werte je Empfänger – für Zähler, Rotmarkierung und Senden-Sperre. */
export function unvollstaendig(z: Zustand): Map<string, string[]> {
  const m = new Map<string, string[]>();
  for (const e of z.empfaenger) {
    const f = fehlend(`${z.betreff}\n${z.text}`, werteVon(z, e), z.eingaben);
    if (f.length) m.set(schluessel(e), f);
  }
  return m;
}

function Eingabe({ f, wert, onChange, noetig }: { f: EingabeFeld; wert: string; onChange: (v: string) => void; noetig: boolean }) {
  // Etappe 5: rot nur, wenn der Brief das Feld braucht (nicht in weggelassenen Abschnitten)
  const rot = noetig && !wert.trim();
  const stil = { ...FELD, height: 40, boxShadow: rot ? 'inset 0 0 0 2px var(--bad)' : FELD.boxShadow };
  // #589: Ankreuzfeld – im Brief ☒ bzw. ☐, nie Pflicht
  if (f.typ === 'ankreuzen') {
    const an = wert === 'Ja';
    return (
      <label className="flex items-center cursor-pointer" style={{ ...FELD, height: 40, gap: 10, padding: '0 12px', boxShadow: an ? 'inset 0 0 0 2px var(--acc)' : FELD.boxShadow }}>
        <input type="checkbox" checked={an} aria-label={f.beschriftung} onChange={(e) => onChange(e.target.checked ? 'Ja' : 'Nein')}
          style={{ width: 18, height: 18, accentColor: 'var(--acc)', cursor: 'pointer' }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: an ? 'var(--ink)' : 'var(--ink3)' }}>{an ? 'Angekreuzt' : 'Nicht angekreuzt'}</span>
      </label>
    );
  }
  if (f.typ === 'datum') return <input type="date" aria-label={f.beschriftung} value={deZuIso(wert)} onChange={(e) => onChange(isoZuDe(e.target.value))} style={stil} />;
  // Etappe 5: Ja/Nein und Auswahl (steuern bedingte Abschnitte)
  if (f.typ === 'janein' || f.typ === 'auswahl') return (
    <select aria-label={f.beschriftung} value={wert} onChange={(e) => onChange(e.target.value)} style={{ ...stil, padding: '0 12px', cursor: 'pointer' }}>
      <option value="">Bitte wählen …</option>
      {(f.typ === 'janein' ? ['Ja', 'Nein'] : f.optionen ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  if (f.typ === 'betrag') return <input inputMode="decimal" aria-label={f.beschriftung} placeholder="0,00" value={wert} onChange={(e) => onChange(e.target.value)} onBlur={() => onChange(betragDe(wert))} style={stil} />;
  return <input aria-label={f.beschriftung} value={wert} onChange={(e) => onChange(e.target.value)} style={stil} />;
}

const DATEN_REF = /\{\{\s*([a-z_]+(?:\.[a-z_]+)+)\s*\}\}/gi;
const hatDaten = (v: string) => new RegExp(DATEN_REF.source, 'i').test(v);
/** „Eigentümer des Objekts · Anschrift“ statt „eigentuemer.anschrift“ */
export const datenLabel = (k: string) => PLATZHALTER_LABEL.get(k.toLowerCase()) ?? k;
const passtZumTyp = (typ: EingabeFeld['typ'], wert: string) =>
  typ === 'datum' ? /^\d{2}\.\d{2}\.\d{4}$/.test(wert) : typ === 'betrag' ? /€/.test(wert) : true;

/** #592: Daten des gewählten Empfängers als durchsuchbare Liste – Auswahl bleibt dynamisch ({{gruppe.feld}}, je Empfänger). */
function DatenAuswahl({ werte, typ, onWahl }: { werte: Record<string, string>; typ: EingabeFeld['typ']; onWahl: (key: string) => void }) {
  const [suche, setSuche] = useState('');
  const eintraege = useMemo(() => Object.entries(werte)
    .filter(([k, v]) => v?.trim() && !k.startsWith('datum.') && passtZumTyp(typ, v))
    .map(([k, v]) => ({ k, label: datenLabel(k), v }))
    .sort((a, b) => a.label.localeCompare(b.label, 'de')), [werte, typ]);
  const q = suche.trim().toLowerCase();
  const treffer = q ? eintraege.filter((x) => `${x.label} ${x.v}`.toLowerCase().includes(q)) : eintraege;
  return (
    <Popover label="Aus Daten wählen" breite={340}
      ausloeser={({ offen, umschalten, ref }) => (
        <button ref={ref} type="button" onClick={umschalten} aria-haspopup="listbox" aria-expanded={offen} aria-label="Aus Daten wählen"
          data-tip="Aus den Daten wählen – wird je Empfänger gefüllt"
          style={{ width: 40, height: 40, flexShrink: 0, borderRadius: 12, border: 'none', cursor: 'pointer', background: offen ? 'var(--acc-soft)' : 'var(--fill2)', color: offen ? 'var(--acc)' : 'var(--ink2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Database size={17} weight="duotone" />
        </button>
      )}>
      {(schliessen) => (
        <div className="flex flex-col" style={{ gap: 6, padding: 4 }}>
          <span className="flex items-center" style={{ gap: 8, height: 38, padding: '0 10px', borderRadius: 10, background: 'var(--fill2)' }}>
            <MagnifyingGlass size={14} color="var(--ink3)" />
            <input autoFocus value={suche} onChange={(ev) => setSuche(ev.target.value)} placeholder="Daten durchsuchen …" aria-label="Daten durchsuchen"
              style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 13 }} />
          </span>
          <div role="listbox" className="flex flex-col" style={{ maxHeight: 300, overflowY: 'auto', gap: 2 }}>
            {treffer.length === 0 && <span style={{ fontSize: 12.5, color: 'var(--ink3)', padding: 8 }}>{eintraege.length ? 'Nichts gefunden' : 'Für diesen Empfänger liegen keine passenden Daten vor'}</span>}
            {treffer.map((x) => (
              <button key={x.k} type="button" role="option" aria-selected={false} onClick={() => { onWahl(x.k); schliessen(); }}
                className="immo-hover-flaeche flex flex-col text-left" style={{ gap: 1, padding: '7px 10px', borderRadius: 10, border: 'none', background: 'transparent', color: 'var(--ink)', cursor: 'pointer', fontFamily: 'inherit' }}>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--ink3)' }}>{x.label}</span>
                <span style={{ fontSize: 13, fontWeight: 600, overflowWrap: 'anywhere' }}>{x.v}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </Popover>
  );
}

export function SchrittInhalt({ zustand: z, setZustand }: { zustand: Zustand; setZustand: SetZustand }) {
  const { mobil } = useMobilModus();
  const [ansicht, setAnsicht] = useState<'text' | 'vorschau'>('text');
  const [index, setIndex] = useState(0);
  const [vorschau, setVorschau] = useState<string | null>(null);
  const [vorschauFehler, setVorschauFehler] = useState<string | null>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const [platzSuche, setPlatzSuche] = useState('');
  const betreffAktiv = useRef(false);
  const gestaltung = useGestaltung();
  const kopf = standardBriefkopf(gestaltung.koepfe, z.briefkopfId);
  const absender = gestaltung.absender.find((a) => a.id === z.absenderId) ?? null;

  const felder = useMemo(() => eingabeFelder(z.betreff, z.text), [z.betreff, z.text]);
  const offen = useMemo(() => unvollstaendig(z), [z]);
  const offeneBeschriftungen = useMemo(() => new Set([...offen.values()].flat()), [offen]);
  const e = z.empfaenger[Math.min(index, z.empfaenger.length - 1)] ?? null;
  const fehltBeiE = e ? (offen.get(schluessel(e)) ?? []).filter((k) => k.includes('.')) : [];

  // Vorschau: echtes Post-PDF, 400 ms nach der letzten Änderung
  useEffect(() => {
    if (!e || !gestaltung.bereit) { setVorschau(null); return; }
    let aktiv = true;
    const t = setTimeout(async () => {
      try {
        const werte = werteVon(z, e);
        const text = mitAnlagenverzeichnis(fuelle(z.text, werte, z.eingaben), z.anlagen);
        const g = await ladePdfGestaltung(kopf, absender);
        const pdf = erzeugeBlankoBriefPdf({ empfaenger: e.anschrift, ort: g.ort, datum: new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' }), betreff: fuelle(z.betreff, werte, z.eingaben), text },
          g.hintergrund, true, await ladePostSchrift(), g.zusatz);
        const url = URL.createObjectURL(pdf.output('blob'));
        if (aktiv) { setVorschau((alt) => { if (alt) URL.revokeObjectURL(alt); return url; }); setVorschauFehler(null); } else URL.revokeObjectURL(url);
      } catch (err) { if (aktiv) setVorschauFehler((err as Error).message); }
    }, 400);
    return () => { aktiv = false; clearTimeout(t); };
  }, [z, e, gestaltung.bereit, kopf, absender]);

  const einfuegen = (key: string) => {
    const marke = `{{${key}}}`;
    if (betreffAktiv.current) { setZustand((x) => ({ ...x, betreff: `${x.betreff}${x.betreff.endsWith(' ') || !x.betreff ? '' : ' '}${marke}` })); return; }
    const el = textRef.current;
    const von = el?.selectionStart ?? z.text.length, bis = el?.selectionEnd ?? von;
    setZustand((x) => ({ ...x, text: x.text.slice(0, von) + marke + x.text.slice(bis) }));
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(von + marke.length, von + marke.length); });
  };
  const setzeWert = (k: string, v: string) => e && setZustand((x) => ({ ...x, werteNeu: { ...x.werteNeu, [schluessel(e)]: { ...(x.werteNeu[schluessel(e)] ?? {}), [k]: v } } }));

  const karte: React.CSSProperties = { borderRadius: 22, padding: mobil ? 12 : 16, gap: 10 };
  const aktiveKoepfe = gestaltung.koepfe.filter((k) => k.aktiv);
  const aktiveAbsender = gestaltung.absender.filter((a) => a.aktiv);
  const links = (
    <div className="flex min-w-0 flex-col" style={{ gap: 12 }}>
      {(aktiveKoepfe.length > 1 || aktiveAbsender.length > 0) && (
        <div className="immo-glas grid" style={{ ...karte, gridTemplateColumns: mobil ? '1fr' : '1fr 1fr' }}>
          {aktiveKoepfe.length > 1 && (
            <label className="flex flex-col" style={{ gap: 6 }}>
              <span style={LABEL}>Briefkopf</span>
              <select value={kopf.id} onChange={(ev) => setZustand((x) => ({ ...x, briefkopfId: ev.target.value }))} style={{ ...FELD, height: 44, padding: '0 12px', cursor: 'pointer' }}>
                {aktiveKoepfe.map((k) => <option key={k.id} value={k.id}>{k.name}{k.ist_standard ? ' (Standard)' : ''}</option>)}
              </select>
            </label>
          )}
          {aktiveAbsender.length > 0 && (
            <label className="flex flex-col" style={{ gap: 6 }}>
              <span style={LABEL}>Absender mit Unterschrift</span>
              <select value={absender?.id ?? ''} onChange={(ev) => setZustand((x) => ({ ...x, absenderId: ev.target.value || null }))} style={{ ...FELD, height: 44, padding: '0 12px', cursor: 'pointer' }}>
                <option value="">Ohne – nur die Grußformel im Text</option>
                {aktiveAbsender.map((a) => <option key={a.id} value={a.id}>{a.name}{a.funktion ? ` · ${a.funktion}` : ''}</option>)}
              </select>
              {absender?.grussformel?.trim() && (
                <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Im Brief endet der Text mit „{absender.grussformel.trim().replace(/\n/g, ' · ')}“ statt mit der Grußformel der Vorlage.</span>
              )}
            </label>
          )}
        </div>
      )}
      <div className="immo-glas flex flex-col" style={karte}>
        <label className="flex flex-col" style={{ gap: 6 }}>
          <span style={LABEL}>Betreff</span>
          <input value={z.betreff} onChange={(ev) => setZustand((x) => ({ ...x, betreff: ev.target.value }))} onFocus={() => { betreffAktiv.current = true; }} style={{ ...FELD, height: 44 }} />
        </label>
        <label className="flex flex-col" style={{ gap: 6 }}>
          <span style={LABEL}>Text – Platzhalter in {'{{…}}'} werden je Empfänger gefüllt</span>
          <textarea ref={textRef} rows={mobil ? 12 : 16} value={z.text} onChange={(ev) => setZustand((x) => ({ ...x, text: ev.target.value }))}
            onFocus={() => { betreffAktiv.current = false; }} style={{ ...FELD, resize: 'vertical', lineHeight: 1.5, padding: '10px 12px' }} />
        </label>
        <details>
          <summary style={{ fontSize: 12.5, fontWeight: 700, cursor: 'pointer', color: 'var(--ink2)' }}>Platzhalter einfügen</summary>
          <input value={platzSuche} onChange={(ev) => setPlatzSuche(ev.target.value)} placeholder="Platzhalter suchen …" aria-label="Platzhalter suchen"
            style={{ ...FELD, height: 36, marginTop: 8 }} />
          <div className="flex flex-wrap" style={{ gap: 6, marginTop: 8 }}>
            {VERFUEGBARE_PLATZHALTER.flatMap((g) => g.eintraege.map((p) => ({ ...p, label: `${g.gruppe}: ${p.label}` })))
              .filter((p) => !platzSuche.trim() || p.label.toLowerCase().includes(platzSuche.trim().toLowerCase())).map((p) => (
              <button key={p.key} type="button" onMouseDown={(ev) => ev.preventDefault()} onClick={() => einfuegen(p.key)}
                style={{ minHeight: 32, padding: '0 10px', borderRadius: 10, border: 'none', background: 'var(--fill2)', color: 'var(--ink)', fontSize: 12, cursor: 'pointer' }}>{p.label}</button>
            ))}
          </div>
        </details>
      </div>

      <div className="immo-glas flex flex-col" style={karte}>
        <span style={{ fontSize: 14, fontWeight: 800 }}>Angaben</span>
        {felder.length === 0 && fehltBeiE.length === 0 && <span style={{ fontSize: 12.5, color: 'var(--ink3)' }}>Keine Angaben nötig – alle Platzhalter sind gefüllt.</span>}
        {felder.map((f) => {
          const roh = z.eingaben[f.schluessel];
          const wert = roh ?? vorgabeWert(f) ?? '';
          const setze = (v: string | undefined) => setZustand((x) => {
            const eingaben = { ...x.eingaben };
            if (v === undefined) delete eingaben[f.schluessel]; else eingaben[f.schluessel] = v;
            return { ...x, eingaben };
          });
          const ausDaten = hatDaten(wert);
          const werteE = e ? werteVon(z, e) : {};
          const quellen = [...wert.matchAll(new RegExp(DATEN_REF.source, 'gi'))].map((m) => datenLabel(m[1]));
          const auswahl = f.typ === 'janein' || f.typ === 'auswahl' || f.typ === 'ankreuzen';
          // zurück zur Vorgabe der Vorlage, wenn sie überschrieben wurde
          const zurueck = roh !== undefined && vorgabeWert(f) !== undefined && roh !== vorgabeWert(f);
          return (
            <div key={f.schluessel} className="flex flex-col" style={{ gap: 4 }}>
              <span className="flex items-center flex-wrap" style={{ ...LABEL, gap: 6 }}>
                {f.beschriftung}
                <span style={{ color: 'var(--ink3)', fontWeight: 600 }}>{ausDaten ? `· aus Daten, je Empfänger` : f.typ === 'ankreuzen' ? '· ankreuzen, für alle' : '· für alle'}</span>
                {zurueck && (
                  <button type="button" onClick={() => setze(undefined)} data-tip="Wieder die Vorbelegung der Vorlage verwenden"
                    className="inline-flex items-center" style={{ gap: 4, border: 'none', background: 'transparent', color: 'var(--acc)', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
                    <ArrowCounterClockwise size={12} weight="bold" /> Vorbelegung
                  </button>
                )}
              </span>
              <span className="flex items-center" style={{ gap: 6 }}>
                {ausDaten ? (
                  <span className="flex flex-1 min-w-0 flex-col justify-center" style={{ ...FELD, minHeight: 40, height: 'auto', padding: '6px 12px', gap: 1,
                    boxShadow: e && hatDaten(datenIn(wert, werteE)) ? 'inset 0 0 0 2px var(--bad)' : FELD.boxShadow }}>
                    <span style={{ fontSize: 13, fontWeight: 600, overflowWrap: 'anywhere' }}>{e ? (hatDaten(datenIn(wert, werteE)) ? 'Für diesen Empfänger nicht hinterlegt' : datenIn(wert, werteE)) : 'wird je Empfänger gefüllt'}</span>
                    <span style={{ fontSize: 11, color: 'var(--ink3)' }}>{quellen.join(' + ')}</span>
                  </span>
                ) : (
                  <span className="flex-1 min-w-0">
                    <Eingabe f={f} wert={wert} noetig={!z.empfaenger.length || offeneBeschriftungen.has(f.beschriftung)} onChange={(v) => setze(v)} />
                  </span>
                )}
                {ausDaten && (
                  <button type="button" onClick={() => setze(e ? datenIn(wert, werteE).replace(new RegExp(DATEN_REF.source, 'gi'), '') : '')} aria-label="Anpassen"
                    data-tip="Anpassen – den Wert als Text übernehmen und ändern (gilt dann für alle Empfänger)"
                    style={{ width: 40, height: 40, flexShrink: 0, borderRadius: 12, border: 'none', cursor: 'pointer', background: 'var(--fill2)', color: 'var(--ink2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <PencilSimple size={16} weight="bold" />
                  </button>
                )}
                {!auswahl && e && <DatenAuswahl werte={werteE} typ={f.typ} onWahl={(k) => setze(`{{${k}}}`)} />}
              </span>
            </div>
          );
        })}
        {e && fehltBeiE.map((k) => (
          <label key={k} className="flex flex-col" style={{ gap: 4 }}>
            <span style={LABEL}>{datenLabel(k)} <span style={{ color: 'var(--ink3)', fontWeight: 600 }}>· fehlt bei {e.name}</span></span>
            <input value={z.werteNeu[schluessel(e)]?.[k] ?? ''} onChange={(ev) => setzeWert(k, ev.target.value)} style={{ ...FELD, height: 40, boxShadow: 'inset 0 0 0 2px var(--bad)' }} />
          </label>
        ))}
        {offen.size > 0 && (
          <span role="status" className="flex items-start" style={{ gap: 6, fontSize: 12.5, color: 'var(--bad)' }}>
            <WarningCircle size={14} weight="fill" style={{ marginTop: 2 }} />
            {offen.size} von {z.empfaenger.length} Empfängern unvollständig – sie werden erst verschickt, wenn alles gefüllt ist (oder abwählen).
          </span>
        )}
      </div>
    </div>
  );

  const rechts = (
    <div className="immo-glas flex min-w-0 flex-col" style={{ ...karte, position: mobil ? undefined : 'sticky', top: 12 }}>
      <div className="flex items-center justify-between" style={{ gap: 8 }}>
        <span className="truncate" style={{ fontSize: 13, fontWeight: 800 }}>
          Vorschau{e ? ` – ${e.name}` : ''}{e && offen.has(schluessel(e)) ? ' (unvollständig)' : ''}
        </span>
        {z.empfaenger.length > 1 && (
          <span className="flex items-center" style={{ gap: 6 }}>
            <button type="button" aria-label="Vorheriger Empfänger" onClick={() => setIndex((i) => Math.max(0, i - 1))} disabled={index === 0}
              style={{ width: 34, height: 34, borderRadius: 10, border: 'none', background: 'var(--fill2)', cursor: 'pointer', opacity: index === 0 ? 0.4 : 1 }}><CaretLeft size={14} weight="bold" /></button>
            <span className="immo-zahl" style={{ fontSize: 12, fontWeight: 700 }}>{index + 1} / {z.empfaenger.length}</span>
            <button type="button" aria-label="Nächster Empfänger" onClick={() => setIndex((i) => Math.min(z.empfaenger.length - 1, i + 1))} disabled={index >= z.empfaenger.length - 1}
              style={{ width: 34, height: 34, borderRadius: 10, border: 'none', background: 'var(--fill2)', cursor: 'pointer', opacity: index >= z.empfaenger.length - 1 ? 0.4 : 1 }}><CaretRight size={14} weight="bold" /></button>
          </span>
        )}
      </div>
      {(vorschauFehler || gestaltung.fehler) && <span role="alert" style={{ fontSize: 12.5, color: 'var(--bad)' }}>{gestaltung.fehler ?? vorschauFehler}</span>}
      {vorschau
        ? <iframe title="Vorschau" src={`${vorschau}#view=FitH`} style={{ width: '100%', height: mobil ? '70dvh' : 'calc(100dvh - 260px)', minHeight: 480, border: 'none', borderRadius: 12, background: '#fff' }} />
        : <div className="animate-pulse" style={{ height: 480, borderRadius: 12, background: 'var(--fill2)' }} />}
    </div>
  );

  if (mobil) {
    return (
      <section className="flex flex-col" style={{ gap: 10 }}>
        <div className="self-start"><Umschalter hoehe={44} wert={ansicht} onChange={setAnsicht} optionen={[{ key: 'text', label: 'Text & Angaben' }, { key: 'vorschau', label: 'Vorschau' }]} /></div>
        {ansicht === 'text' ? links : rechts}
      </section>
    );
  }
  return <section className="grid items-start" style={{ gridTemplateColumns: 'minmax(0,5fr) minmax(0,6fr)', gap: 14 }}>{links}{rechts}</section>;
}
